import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useMembership } from '../../contexts/MembershipContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {
  FiPackage,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiX,
  FiCheck,
  FiDollarSign,
  FiCalendar,
  FiFileText,
  FiUsers,
  FiStar,
  FiToggleLeft,
  FiToggleRight
} = FiIcons;

const MembershipPlans = () => {
  const { user } = useAuth();
  const { subscribeToPlan, getMembershipDetails } = useMembership();
  const isAdmin = user?.role === 'admin';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activatingId, setActivatingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    plan_name: '',
    price: '',
    duration: '',
    duration_unit: 'months',
    applications_included: '',
    features: [''],
    status: 'Active'
  });

  // Mock data for demonstration
  const mockPlans = [
    {
      id: 'PLAN001',
      plan_name: 'Student Basic',
      price: 999,
      duration: 3,
      duration_unit: 'months',
      applications_included: 5,
      features: [
        'Basic application processing',
        'Email support',
        'Document verification',
        'Standard processing time'
      ],
      status: 'Active',
      created_at: '2024-01-10T09:00:00Z',
      subscribers_count: 45
    },
    {
      id: 'PLAN002',
      plan_name: 'Student Pro',
      price: 1999,
      duration: 6,
      duration_unit: 'months',
      applications_included: 15,
      features: [
        'Priority application processing',
        '24/7 phone support',
        'Document verification',
        'Fast processing time',
        'Free document pickup',
        'Application tracking'
      ],
      status: 'Active',
      created_at: '2024-01-08T14:30:00Z',
      subscribers_count: 28
    },
    {
      id: 'PLAN003',
      plan_name: 'Premium',
      price: 3999,
      duration: 12,
      duration_unit: 'months',
      applications_included: 50,
      features: [
        'Premium application processing',
        'Dedicated support manager',
        'Document verification',
        'Express processing time',
        'Free document pickup',
        'Application tracking',
        'No service charges',
        'Unlimited consultations'
      ],
      status: 'Active',
      created_at: '2024-01-05T11:15:00Z',
      subscribers_count: 12
    },
    {
      id: 'PLAN004',
      plan_name: 'Trial Plan',
      price: 299,
      duration: 1,
      duration_unit: 'months',
      applications_included: 2,
      features: [
        'Basic application processing',
        'Email support',
        'Document verification'
      ],
      status: 'Inactive',
      created_at: '2024-01-03T16:45:00Z',
      subscribers_count: 8
    }
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    filterPlans();
  }, [plans, searchQuery, statusFilter]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      // In a real app, you would fetch from Supabase
      // const { data, error } = await supabase
      //   .from('membership_plans')
      //   .select('*')
      //   .order('created_at', { ascending: false });

      // For now, using mock data
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
      setPlans(mockPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load membership plans');
    } finally {
      setLoading(false);
    }
  };

  const filterPlans = () => {
    let filtered = [...plans];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(plan =>
        plan.plan_name.toLowerCase().includes(query) ||
        plan.id.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(plan => plan.status === statusFilter);
    }

    setFilteredPlans(filtered);
  };

  const handleCustomerActivate = async (plan) => {
    try {
      setActivatingId(plan.id);
      await new Promise(r => setTimeout(r, 1200));
      const paymentDetails = {
        amount: plan.price,
        paymentMethod: 'credit_card',
        transactionId: `txn_${Date.now()}`
      };
      await subscribeToPlan(plan.id, paymentDetails);
      const _updated = getMembershipDetails?.();
      toast.success('Membership activated successfully');
    } catch (e) {
      toast.error('Unable to activate membership');
      console.error(e);
    } finally {
      setActivatingId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' || name === 'duration' || name === 'applications_included'
        ? value === '' ? '' : Number(value)
        : value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeatureField = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeatureField = (index) => {
    const newFeatures = [...formData.features];
    newFeatures.splice(index, 1);
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const validateForm = () => {
    if (!formData.plan_name.trim()) {
      toast.error('Plan name is required');
      return false;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error('Price must be greater than 0');
      return false;
    }
    if (!formData.duration || formData.duration <= 0) {
      toast.error('Duration must be greater than 0');
      return false;
    }
    if (!formData.applications_included || formData.applications_included <= 0) {
      toast.error('Number of applications must be greater than 0');
      return false;
    }

    // Filter out empty features
    const filteredFeatures = formData.features.filter(feature => feature.trim() !== '');
    if (filteredFeatures.length === 0) {
      toast.error('Please add at least one feature');
      return false;
    }

    return true;
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    setFormData({
      plan_name: '',
      price: '',
      duration: '',
      duration_unit: 'months',
      applications_included: '',
      features: [''],
      status: 'Active'
    });
    setShowAddModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_name: plan.plan_name,
      price: plan.price,
      duration: plan.duration,
      duration_unit: plan.duration_unit,
      applications_included: plan.applications_included,
      features: plan.features.length > 0 ? plan.features : [''],
      status: plan.status
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const planData = {
        ...formData,
        features: formData.features.filter(feature => feature.trim() !== ''),
        updated_at: new Date().toISOString()
      };

      let result;
      if (editingPlan) {
        // Update existing plan
        // const { data, error } = await supabase
        //   .from('membership_plans')
        //   .update(planData)
        //   .eq('id', editingPlan.id)
        //   .select();

        // if (error) throw error;
        // result = data[0];
        
        // Update local state
        result = { ...editingPlan, ...planData };
        setPlans(plans.map(plan => 
          plan.id === editingPlan.id ? result : plan
        ));
        
        toast.success('Membership plan updated successfully');
      } else {
        // Create new plan
        // const { data, error } = await supabase
        //   .from('membership_plans')
        //   .insert(planData)
        //   .select();

        // if (error) throw error;
        // result = data[0];
        
        // Add to local state
        result = {
          id: `PLAN${String(plans.length + 1).padStart(3, '0')}`,
          ...planData,
          created_at: new Date().toISOString(),
          subscribers_count: 0
        };
        setPlans([...plans, result]);
        
        toast.success('Membership plan created successfully');
      }
      
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save membership plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (window.confirm('Are you sure you want to delete this membership plan? This action cannot be undone.')) {
      try {
        // const { error } = await supabase
        //   .from('membership_plans')
        //   .delete()
        //   .eq('id', planId);

        // if (error) throw error;
        
        setPlans(plans.filter(plan => plan.id !== planId));
        toast.success('Membership plan deleted successfully');
      } catch (error) {
        console.error('Error deleting plan:', error);
        toast.error('Failed to delete membership plan');
      }
    }
  };

  const handleToggleStatus = async (planId) => {
    try {
      const plan = plans.find(p => p.id === planId);
      const newStatus = plan.status === 'Active' ? 'Inactive' : 'Active';

      // const { error } = await supabase
      //   .from('membership_plans')
      //   .update({ status: newStatus })
      //   .eq('id', planId);

      // if (error) throw error;
      
      setPlans(plans.map(p =>
        p.id === planId ? { ...p, status: newStatus } : p
      ));
      
      toast.success(`Plan ${newStatus.toLowerCase()} successfully`);
    } catch (error) {
      console.error('Error updating plan status:', error);
      toast.error('Failed to update plan status');
    }
  };

  const getStatusColor = (status) => {
    return status === 'Active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Membership Plans</h1>
              <p className="text-gray-600 mt-1">
                Manage membership plans and pricing
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={fetchPlans}
                className="flex items-center"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleAddPlan}
                className="flex items-center"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Add Plan
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <SafeIcon icon={FiPackage} className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Plans</p>
                <p className="text-2xl font-semibold text-gray-900">{plans.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Plans</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {plans.filter(plan => plan.status === 'Active').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Subscribers</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {plans.reduce((sum, plan) => sum + plan.subscribers_count, 0)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full">
                <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Price</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {plans.length > 0 
                    ? formatCurrency(plans.reduce((sum, plan) => sum + plan.price, 0) / plans.length)
                    : '₹0'
                  }
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by plan name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <p className="text-sm text-gray-600">
                Showing {filteredPlans.length} of {plans.length} plans
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Plans Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredPlans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="p-6 relative">
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(plan.status)}`}>
                      {plan.status}
                    </span>
                  </div>

                  {/* Plan Header */}
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{plan.plan_name}</h3>
                    <p className="text-sm text-gray-500 mb-3">Plan ID: {plan.id}</p>
                    <div className="text-3xl font-bold text-primary-600 mb-2">
                      {formatCurrency(plan.price)}
                    </div>
                    <p className="text-sm text-gray-600">
                      per {plan.duration} {plan.duration_unit}
                    </p>
                  </div>

                  {/* Plan Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm">
                      <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">Applications:</span>
                      <span className="ml-auto font-medium">{plan.applications_included}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">Subscribers:</span>
                      <span className="ml-auto font-medium">{plan.subscribers_count}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">Created:</span>
                      <span className="ml-auto font-medium">{formatDate(plan.created_at)}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <SafeIcon icon={FiStar} className="w-4 h-4 mr-2" />
                      Features
                    </h4>
                    <ul className="space-y-2">
                      {plan.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                      {plan.features.length > 3 && (
                        <li className="text-sm text-gray-500">
                          +{plan.features.length - 3} more features
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditPlan(plan)}
                        className="flex-1"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(plan.id)}
                      className="flex items-center"
                    >
                      <SafeIcon 
                        icon={plan.status === 'Active' ? FiToggleRight : FiToggleLeft} 
                        className="w-4 h-4" 
                      />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeletePlan(plan.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <SafeIcon icon={FiPackage} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No membership plans found</h3>
            <p className="text-gray-600 mb-6">Create your first membership plan to get started</p>
            <Button onClick={handleAddPlan}>
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Add Plan
            </Button>
          </Card>
        )}
      </div>

      {/* Add/Edit Plan Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingPlan ? 'Edit Membership Plan' : 'Add New Membership Plan'}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Plan Name *"
                name="plan_name"
                value={formData.plan_name}
                onChange={handleInputChange}
                placeholder="e.g., Student Basic, Premium"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Price (₹) *"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="999"
                  min="0"
                  required
                />
                <Input
                  label="Applications Included *"
                  name="applications_included"
                  type="number"
                  value={formData.applications_included}
                  onChange={handleInputChange}
                  placeholder="5"
                  min="1"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Duration *"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder="3"
                  min="1"
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration Unit
                  </label>
                  <select
                    name="duration_unit"
                    value={formData.duration_unit}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="months">Months</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Features/Benefits *
                </label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder="e.g., Unlimited Study Material Access"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {formData.features.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFeatureField(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFeatureField}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  + Add Feature
                </button>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MembershipPlans;
